/**
 * block-templates — the catalog of insertable block kinds for the add-block (+)
 * menu, plus the canonical TEMPLATE source each one drops into the document.
 *
 * Each template is UNINDENTED `.ls` surface syntax (single or multi-line). The
 * caller (EditorView) re-indents every inserted line to the sibling depth at the
 * insertion point before splicing, so a block dropped inside a `@choice` body
 * lines up with its neighbours.
 *
 * Every template is designed to `parseLsBlocks` back to the advertised kind and
 * to keep braces balanced, so a round-trip after insert is always valid.
 *
 * i18n: labels are `Entry` literals resolved via `useTr()` at render time; this
 * module is pure data + string helpers (no React), so it stays trivially
 * unit-testable.
 */
import type { Entry } from "../../i18n/context.js";

export const MINIGAME_PRESETS = [
  {
    id: "qte-boss-parry",
    label: { zh: "格挡反击", en: "Boss Parry" },
    description: "Read the enemy wind-up, parry inside a short timing window, and chain successful counters to survive escalating pressure.",
  },
  {
    id: "lane-dash",
    label: { zh: "追逐闪避", en: "Lane Dash" },
    description: "Switch lanes quickly to dodge hazards and keep sprinting as speed ramps up.",
  },
  {
    id: "cannon-aim",
    label: { zh: "精准命中", en: "Cannon Aim" },
    description: "Adjust aim and timing to hit moving targets or a critical point with precision.",
  },
  {
    id: "will-surge",
    label: { zh: "顶住压力", en: "Will Surge" },
    description: "Tap rapidly to keep a willpower bar from collapsing while periodic surge waves spike the pressure until the timer ends.",
  },
  {
    id: "red-light-green-light",
    label: { zh: "红灯定格", en: "Stop-Go" },
    description: "Move forward during green light, freeze instantly when the light turns red; any movement during red light costs a penalty.",
  },
  {
    id: "qte-hold-release",
    label: { zh: "蓄力放手", en: "Hold Release" },
    description: "Hold to charge, wait for the exact release window, then let go at the right moment.",
  },
  {
    id: "conveyor-sort",
    label: { zh: "线索分类", en: "Conveyor Sort" },
    description: "Drag parcels from a moving conveyor into the correct bins before they fall off; speed increases and new categories appear over time.",
  },
  {
    id: "parking-rush",
    label: { zh: "挪车解路", en: "Parking Rush" },
    description: "Reposition vehicles or blockers in the right order to open a path with minimal wasted moves.",
  },
  {
    id: "maze-escape",
    label: { zh: "迷宫逃脱", en: "Maze Escape" },
    description: "Find the exit or key route through a maze while minimizing wrong turns and dead ends.",
  },
  {
    id: "color-match",
    label: { zh: "读懂气氛", en: "Color Match" },
    description: "Read the color cue and tap the matching swatch as fast as possible; rounds speed up and distractors increase.",
  },
  {
    id: "spotlight-seek",
    label: { zh: "聚光寻找", en: "Spotlight Seek" },
    description: "Sweep a spotlight through the dark to reveal the person or object that matters.",
  },
  {
    id: "stardew-fishing",
    label: { zh: "情绪拉扯", en: "Stardew Fishing" },
    description: "Control a floating marker with careful holds and releases so it stays attached to a moving target zone.",
  },
] as const satisfies readonly {
  readonly id: string;
  readonly label: Entry;
  readonly description: string;
}[];

export type MinigamePresetId = (typeof MINIGAME_PRESETS)[number]["id"];
export type MinigamePresetKind = `minigame:${MinigamePresetId}`;

/** The block kinds the add-block menu can insert. */
export type InsertKind =
  | "dialogue"
  | "narration"
  | "you"
  | "bg"
  | "char"
  | "music"
  | "sfx"
  | "cg"
  | "phone"
  | "choice"
  | "gate"
  | "affection"
  | "signal"
  | "butterfly"
  | "trick"
  | "minigame"
  | MinigamePresetKind
  | "achievement"
  | "pause";

/** A menu entry: a kind, its bilingual label, and the token that tints it. */
export interface InsertOption {
  readonly kind: InsertKind;
  readonly label: Entry;
  /** The `--kind-*` token the little swatch is tinted with. */
  readonly token: string;
}

/**
 * The ordered add-block menu. Order follows the writing flow: speech first,
 * then staging, then structural / state directives, then the pause beat.
 */
export const INSERT_OPTIONS: readonly InsertOption[] = [
  { kind: "dialogue", label: { zh: "对白", en: "Dialogue" }, token: "--kind-dialogue" },
  { kind: "narration", label: { zh: "旁白", en: "Narration" }, token: "--kind-dialogue" },
  { kind: "you", label: { zh: "内心独白", en: "Inner voice (YOU)" }, token: "--kind-dialogue" },
  { kind: "bg", label: { zh: "背景", en: "Background" }, token: "--kind-bg" },
  { kind: "char", label: { zh: "立绘", en: "Character" }, token: "--kind-character" },
  { kind: "music", label: { zh: "音乐", en: "Music" }, token: "--kind-music" },
  { kind: "sfx", label: { zh: "音效", en: "Sound effect" }, token: "--kind-sfx" },
  { kind: "cg", label: { zh: "CG", en: "CG" }, token: "--kind-cg" },
  { kind: "phone", label: { zh: "手机消息", en: "Phone messages" }, token: "--kind-phone" },
  { kind: "choice", label: { zh: "选择分支", en: "Choice branch" }, token: "--kind-choice" },
  { kind: "gate", label: { zh: "结局路线", en: "Exit routes" }, token: "--kind-gate" },
  { kind: "affection", label: { zh: "好感", en: "Affection" }, token: "--kind-affection" },
  { kind: "signal", label: { zh: "信号", en: "Signal" }, token: "--kind-signal" },
  { kind: "butterfly", label: { zh: "蝴蝶记忆", en: "Butterfly memory" }, token: "--kind-record" },
  { kind: "trick", label: { zh: "体感互动", en: "Trick" }, token: "--kind-trick" },
  { kind: "minigame", label: { zh: "小游戏", en: "Minigame" }, token: "--kind-minigame" },
  { kind: "achievement", label: { zh: "成就", en: "Achievement" }, token: "--kind-record" },
  { kind: "pause", label: { zh: "停顿", en: "Pause" }, token: "--kind-pause" },
];

export function minigamePresetKind(id: MinigamePresetId): MinigamePresetKind {
  return `minigame:${id}`;
}

export function minigamePresetInsertOptions(): readonly InsertOption[] {
  return MINIGAME_PRESETS.map((preset) => ({
    kind: minigamePresetKind(preset.id),
    label: preset.label,
    token: "--kind-minigame",
  }));
}

export function expandedInsertOptions(): readonly InsertOption[] {
  return INSERT_OPTIONS.flatMap((option) => (
    option.kind === "minigame" ? minigamePresetInsertOptions() : [option]
  ));
}

export function minigamePresetForKind(kind: InsertKind): (typeof MINIGAME_PRESETS)[number] | undefined {
  if (!kind.startsWith("minigame:")) return undefined;
  const id = kind.slice("minigame:".length);
  return MINIGAME_PRESETS.find((preset) => preset.id === id);
}

export function isMinigamePresetKind(kind: InsertKind): kind is MinigamePresetKind {
  return Boolean(minigamePresetForKind(kind));
}

function lsQuote(value: string): string {
  return `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

/**
 * The canonical UNINDENTED template source for an insertable kind. Multi-line
 * templates (choice) use `\n`; the caller re-indents.
 *
 * The placeholder text is intentionally minimal-but-valid so the inserted block
 * both re-parses and reads sensibly in the card view immediately after insert.
 */
export function templateFor(kind: InsertKind): string {
  if (isMinigamePresetKind(kind)) {
    const preset = minigamePresetForKind(kind)!;
    return `@minigame ${preset.id} ${lsQuote(preset.description)}`;
  }

  switch (kind) {
    case "dialogue":
      return "LUNA: ……";
    case "narration":
      return "NARRATOR: ……";
    case "you":
      return "YOU: ……";
    case "bg":
      return "@bg set room";
    case "char":
      return "@luna smile";
    case "music":
      return "@music theme";
    case "sfx":
      return "@sfx door";
    case "cg":
      return '@cg key_moment "A clear full-screen illustration of the emotional beat."';
    case "phone":
      return [
        "@phone {",
        "  @text from LUNA: 你到了吗？",
        "}",
      ].join("\n");
    case "choice":
      return [
        "@choice {",
        '  @option A brave "主动回应" {',
        "    check {",
        "      attr: CHA",
        "      dc: 12",
        "    }",
        "    @if (check.success) {",
        "      LUNA: 我听见你了。",
        "    } @else {",
        "      YOU: 我还需要一点勇气。",
        "    }",
        "  }",
        '  @option B safe "先观察" {',
        "    YOU: 我需要再看清楚一点。",
        "  }",
        "}",
      ].join("\n");
    case "gate":
      return "@gate { @end to_be_continued }";
    case "affection":
      return "@affection luna +1";
    case "signal":
      return "@signal mark IMPORTANT_MOMENT";
    case "butterfly":
      return '@butterfly "The player chose to stay present instead of looking away."';
    case "trick":
      return '@trick tap "Tap quickly to steady your breathing."';
    case "minigame":
      return templateFor(minigamePresetKind("color-match"));
    case "achievement":
      return [
        "@achievement FIRST_MEETING {",
        '  name: "First Meeting"',
        "  rarity: uncommon",
        '  description: "You chose to step into the moment instead of walking away."',
        "}",
      ].join("\n");
    case "pause":
      return "@pause";
    default: {
      // Exhaustiveness guard: adding a kind without a template is a type error.
      const never: never = kind;
      return never;
    }
  }
}

/**
 * Default tail route table paired with a newly inserted choice when the episode
 * has no `@gate` yet. Keeping this separate from `templateFor("choice")` lets
 * card-ops place the route at the episode tail instead of immediately after the
 * selected card.
 */
export function defaultChoiceRouteTemplate(): string {
  return [
    "@gate {",
    "  @if (A.any): @next next:01",
    "  @else @if (B.any): @next observe:01",
    "  @else: @end to_be_continued",
    "}",
  ].join("\n");
}
